import { supabase } from '../config/supabase';
import { NewHire } from './hireService';

const BACKEND_EMAIL_URL = 'https://hr-backend-pipeline.vercel.app/api/resend/reminders/send';

function buildOnboardingEmailHtml(email: string): string {
  return `<!DOCTYPE html>
<html lang="en">
  <head>
    <meta charset="UTF-8" />
    <title>Begin Onboarding</title>
  </head>
  <body style="font-family: Arial, sans-serif; color: #333; line-height: 1.6; background-color: #f9f9f9; padding: 20px;">
    <div style="max-width: 600px; margin: auto; background-color: #fff; border-radius: 8px; padding: 30px; box-shadow: 0 0 10px rgba(0,0,0,0.05);">

      <h2 style="color: #1a202c;">🚀 Begin Your Onboarding</h2>

      <p>
        To get started, go to <strong>glonboarding.com</strong> and begin your onboarding process.
      </p>

      <p>
        Sign in using your email address below to access your personal onboarding checklist:
      </p>

      <p style="font-size: 16px; margin: 16px 0;">
        <strong>${email}</strong>
      </p>

      <p style="text-align: center; margin: 24px 0;">
        <a href="https://www.glonboarding.com"
           style="display: inline-block; padding: 12px 24px; background: linear-gradient(135deg, #667eea 0%, #764ba2 50%, #667eea 100%); color: white; text-decoration: none; border-radius: 8px; font-weight: 600; box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3); transition: all 0.3s ease;">
          Go to Glonboarding
        </a>
      </p>

      <p style="font-size: 14px; color: #555;">
        Once there, simply enter your email address and you'll be taken directly to your onboarding steps.
      </p>

      <hr style="margin: 30px 0;">

      <p style="font-size: 13px; color: #999;">
        This message was sent securely. If you have any questions, please contact your onboarding manager.
      </p>

    </div>
  </body>
</html>`;
}

export const unarchiveService = {
  async unarchiveHire(archiveRecord: NewHire): Promise<void> {
    const newId = crypto.randomUUID();

    // Step 1: Send onboarding email via backend Express server — abort if this fails
    const emailResponse = await fetch(BACKEND_EMAIL_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: archiveRecord.email,
        Subject: 'Begin Your Onboarding',
        from: 'Glonboarding <no-reply@glonboarding.com>',
        body: buildOnboardingEmailHtml(archiveRecord.email),
      }),
    });

    if (!emailResponse.ok) {
      const errorBody = await emailResponse.text().catch(() => 'Unknown error');
      throw new Error(`Failed to send onboarding email: ${errorBody}`);
    }

    // Step 2: Insert into new_hire with the new UUID and archive data
    const { error: hireInsertError } = await supabase
      .from('new_hire')
      .insert([{
        id: newId,
        created_at: new Date().toISOString(),
        fullName: archiveRecord.fullName,
        state: archiveRecord.state,
        email: archiveRecord.email,
        mobileNumber: archiveRecord.mobileNumber,
        progress: archiveRecord.progress,
        instagram: archiveRecord.instagram,
        org_id: archiveRecord.org_id,
        update_timestamp: archiveRecord.update_timestamp,
        checkListNum: archiveRecord.checkListNum,
        recruiter_numbers: archiveRecord.recruiter_numbers,
        reference: archiveRecord.reference,
        recruiter_emails: archiveRecord.recruiter_emails,
        agent_appointment_sent: archiveRecord.agent_appointment_sent ?? false,
      }]);

    if (hireInsertError) {
      throw new Error(`Failed to insert into new_hire: ${hireInsertError.message}`);
    }

    // Step 3: Insert into profiles with the same new UUID
    const { error: profileInsertError } = await supabase
      .from('profiles')
      .insert([{
        id: newId,
        role: 'new_hire',
        full_name: archiveRecord.fullName,
        org_id: archiveRecord.org_id,
      }]);

    if (profileInsertError) {
      throw new Error(`Failed to insert into profiles: ${profileInsertError.message}`);
    }

    // Step 4: Delete from new_hire_archive
    const { error: deleteError } = await supabase
      .from('new_hire_archive')
      .delete()
      .eq('id', archiveRecord.id);

    if (deleteError) {
      throw new Error(`Failed to delete from archive: ${deleteError.message}`);
    }
  },
};

export default unarchiveService;
